/**
 * ReportView - branded, print-ready rendering of an AI fleet report.
 *
 * Renders the report's markdown-ish body (H1/H2, tables, bullets, bold)
 * as a styled web page with the CodeNekt for Fleet palette, and offers
 * "Imprimer / PDF" via window.print(): a print stylesheet isolates the
 * report so the browser's "Save as PDF" produces a clean branded PDF.
 */
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Icon } from "../../components/Icon/Icon";
import { colors } from "../../styles/colors";
import { Report, downloadReportPdf } from "../../api/aiReports";

const BLUE = "#2563EB";
const DARK = "#0F172A";
const MUTED = "#64748B";
const BORDER = "#E2E8F0";

// --- tiny markdown renderer (headings, tables, bullets, bold/italic) --------

const inline = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let rest = text;
  let key = 0;
  const re = /\*\*(.+?)\*\*|_(.+?)_/;
  while (rest.length) {
    const m = rest.match(re);
    if (!m || m.index === undefined) {
      parts.push(rest);
      break;
    }
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    if (m[1] !== undefined) {
      parts.push(<strong key={`b${key++}`} style={{ color: DARK }}>{m[1]}</strong>);
    } else {
      parts.push(<em key={`i${key++}`} style={{ color: MUTED }}>{m[2]}</em>);
    }
    rest = rest.slice(m.index + m[0].length);
  }
  return parts;
};

const isSepRow = (cells: string[]) =>
  cells.every((c) => /^:?-{2,}:?$/.test(c || "-"));

const renderBody = (body: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  const lines = body.split("\n");
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i].trimEnd();

    if (line.trim().startsWith("|")) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        const cells = lines[i].trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
        if (!isSepRow(cells)) rows.push(cells);
        i++;
      }
      if (rows.length) {
        const [head, ...bodyRows] = rows;
        out.push(
          <div key={`t${key++}`} style={{ overflowX: "auto", margin: "10px 0 18px" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
              <thead>
                <tr>
                  {head.map((c, j) => (
                    <th key={j} style={{
                      textAlign: "left", padding: "8px 12px",
                      background: "#F1F5F9", color: BLUE,
                      borderBottom: `2px solid ${BLUE}`,
                      fontFamily: "Poppins-Medium", fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}>{inline(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((r, ri) => (
                  <tr key={ri} style={{ background: ri % 2 ? "#F8FAFC" : "transparent" }}>
                    {r.map((c, j) => (
                      <td key={j} style={{
                        padding: "7px 12px", color: DARK,
                        borderBottom: `1px solid ${BORDER}`,
                        fontFamily: "Poppins-Regular",
                      }}>{inline(c)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    if (line.startsWith("# ")) {
      out.push(
        <h1 key={`h${key++}`} style={{
          fontFamily: "Poppins-Medium", fontSize: 24, color: BLUE,
          margin: "0 0 4px", lineHeight: 1.3,
        }}>{inline(line.slice(2))}</h1>
      );
    } else if (line.startsWith("## ")) {
      out.push(
        <h2 key={`h${key++}`} style={{
          fontFamily: "Poppins-Medium", fontSize: 17, color: BLUE,
          margin: "26px 0 4px", paddingBottom: 6,
          borderBottom: `2px solid ${BLUE}`, display: "inline-block",
        }}>{inline(line.slice(3))}</h2>
      );
      out.push(<div key={`br${key++}`} />);
    } else if (line.startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trimEnd().startsWith("- ")) {
        items.push(lines[i].trimEnd().slice(2));
        i++;
      }
      out.push(
        <ul key={`u${key++}`} style={{
          margin: "8px 0 14px", paddingLeft: 22, color: DARK,
          fontFamily: "Poppins-Regular", fontSize: 13.5, lineHeight: 1.8,
        }}>
          {items.map((it, j) => <li key={j}>{inline(it)}</li>)}
        </ul>
      );
      continue;
    } else if (/^_.*_$/.test(line.trim())) {
      out.push(
        <p key={`m${key++}`} style={{
          color: MUTED, fontStyle: "italic", fontSize: 12,
          fontFamily: "Poppins-Regular", margin: "2px 0 10px",
        }}>{line.trim().replace(/^_|_$/g, "")}</p>
      );
    } else if (line.trim()) {
      out.push(
        <p key={`p${key++}`} style={{
          color: DARK, fontSize: 13.5, lineHeight: 1.7,
          fontFamily: "Poppins-Regular", margin: "6px 0",
        }}>{inline(line)}</p>
      );
    }
    i++;
  }
  return out;
};

// --- component ---------------------------------------------------------------

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #codenekt-report-print, #codenekt-report-print * { visibility: visible !important; }
  #codenekt-report-print {
    position: absolute !important; left: 0; top: 0; width: 100%;
    box-shadow: none !important; border: none !important;
  }
  #codenekt-report-print .no-print { display: none !important; }
  @page { margin: 14mm; }
}
`;

interface Props {
  report: Report;
  onBack: () => void;
}

export const ReportView: React.FC<Props> = ({ report, onBack }) => {
  const { t, i18n } = useTranslation();
  const content = useMemo(() => renderBody(report.content), [report.content]);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
      const { blob, filename } = await downloadReportPdf(report.id, lang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("aiAgent.reports.pdfError", "PDF download failed"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "20px 26px 40px" }}>
      <style>{PRINT_CSS}</style>

      {/* Toolbar (hidden when printing) */}
      <div className="no-print" style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 16, gap: 10, flexWrap: "wrap",
      }}>
        <button type="button" onClick={onBack} style={{
          fontFamily: "Poppins-Regular", fontSize: 13, padding: "8px 14px",
          borderRadius: 10, border: `1px solid ${colors.borderLight}`,
          background: "transparent", color: colors.secondaryTextColor,
          cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
        }}>
          <Icon name="chevronLeft" width={15} height={15} color={colors.secondaryTextColor} />
          {t("aiAgent.reports.back", "Back to reports")}
        </button>
        <button type="button" onClick={handleDownloadPdf} disabled={downloading} style={{
          fontFamily: "Poppins-Medium", fontSize: 13, padding: "9px 18px",
          borderRadius: 10, border: "none", background: BLUE, color: "#F8FAFC",
          cursor: downloading ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 8,
          opacity: downloading ? 0.7 : 1,
        }}>
          <Icon name="fileStack" width={15} height={15} color="#F8FAFC" />
          {downloading
            ? t("aiAgent.reports.downloading", "Telechargement...")
            : t("aiAgent.reports.downloadPdf", "Telecharger PDF")}
        </button>
      </div>

      {/* The printable report "page" */}
      <div id="codenekt-report-print" style={{
        background: "#FFFFFF", borderRadius: 14,
        border: `1px solid ${BORDER}`,
        boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
        maxWidth: 860, margin: "0 auto", overflow: "hidden",
      }}>
        {/* Brand band */}
        <div style={{
          background: `linear-gradient(100deg, ${BLUE} 0%, #1D4ED8 100%)`,
          padding: "18px 32px", display: "flex",
          alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{
            fontFamily: "Poppins-Medium", fontSize: 16, color: "#FFFFFF",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <Icon name="sparkles" width={20} height={20} color="#FFFFFF" />
            CodeNekt for Fleet
          </div>
          <div style={{
            fontFamily: "Poppins-Regular", fontSize: 12, color: "rgba(255,255,255,0.85)",
          }}>
            {report.period_label} · {report.frequency}
          </div>
        </div>

        <div style={{ padding: "28px 32px 36px" }}>
          {content}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: `1px solid ${BORDER}`, padding: "12px 32px",
          display: "flex", justifyContent: "space-between",
          fontFamily: "Poppins-Regular", fontSize: 11, color: MUTED,
        }}>
          <span>CodeNekt for Fleet — Rapport IA</span>
          <span>{new Date(report.created_at).toLocaleString("fr-FR")}</span>
        </div>
      </div>
    </div>
  );
};

export default ReportView;
